import {
  DecaCompanyRole,
  getEmptyDecaDocument,
  getEmptyIDecaDocumentCompany,
  getEmptyIDecaDocumentStop,
  IDecaDocument,
  IDecaDocumentCompany,
  IDecaDocumentStop,
} from '@okcargo-apps/shared-domain';
import { DECA_ROLE, otherDecaRole } from './deca-role.utils';

function getCompanyForRole(document: IDecaDocument, role: DecaCompanyRole): IDecaDocumentCompany {
  return role === DECA_ROLE.CARRIER ? document.carrier : document.shipper;
}

export function mapDocumentToRole(
  role: DecaCompanyRole,
  previousRole: DecaCompanyRole | null,
  previousDocument: IDecaDocument | null,
  currentUserCompany: IDecaDocumentCompany,
): IDecaDocument {
  if (!previousRole) {
    return {
      ...getEmptyDecaDocument(),
      [role]: currentUserCompany,
    } as IDecaDocument;
  }

  const base = previousDocument ?? getEmptyDecaDocument();
  const counterparty = getCompanyForRole(base, otherDecaRole(previousRole));

  return {
    ...base,
    shipper: role === DECA_ROLE.SHIPPER ? currentUserCompany : counterparty,
    carrier: role === DECA_ROLE.CARRIER ? currentUserCompany : counterparty,
  };
}

export function mapDocumentToCreationCopy(document: IDecaDocument): IDecaDocument {
  const emptyCompany = getEmptyIDecaDocumentCompany();
  const emptyStop = getEmptyIDecaDocumentStop();

  return {
    ...getEmptyDecaDocument(),
    specialAuthorizationType: document.specialAuthorizationType ?? '',
    tractorPlateNumber: document.tractorPlateNumber ?? '',
    trailerPlateNumber: document.trailerPlateNumber ?? '',
    shipper: copyCompany(document.shipper, emptyCompany),
    carrier: copyCompany(document.carrier, emptyCompany),
    segments: (document.segments ?? []).map((seg) => ({
      id: crypto.randomUUID(),
      goodsDescription: seg.goodsDescription ?? '',
      netWeight: seg.netWeight ?? undefined,
      origin: copyStop(seg.origin, emptyStop),
      destination: copyStop(seg.destination, emptyStop),
    })),
  };
}

export function mapDocumentToTransfer(
  document: IDecaDocument,
  currentUserCompany: IDecaDocumentCompany,
): IDecaDocument {
  const emptyCompany = getEmptyIDecaDocumentCompany();
  const emptyStop = getEmptyIDecaDocumentStop();

  return {
    ...getEmptyDecaDocument(),
    shipper: copyCompany(currentUserCompany, emptyCompany),
    carrier: emptyCompany,
    segments: (document.segments ?? []).map((seg) => ({
      id: crypto.randomUUID(),
      goodsDescription: seg.goodsDescription ?? '',
      netWeight: seg.netWeight ?? undefined,
      origin: copyStop(seg.origin, emptyStop, true),
      destination: copyStop(seg.destination, emptyStop, true),
    })),
  };
}

function copyCompany(
  source: IDecaDocumentCompany,
  empty: ReturnType<typeof getEmptyIDecaDocumentCompany>,
): IDecaDocumentCompany {
  return {
    ...empty,
    id: source.id,
    companyName: source.companyName ?? '',
    taxId: source.taxId ?? '',
    street: source.street ?? '',
    city: source.city ?? '',
    postalCode: source.postalCode ?? '',
    stateCode: source.stateCode ?? '',
    countryCode: source.countryCode ?? '',
    contactId: source.contactId,
    contactFirstName: source.contactFirstName ?? '',
    contactLastName: source.contactLastName ?? '',
    contactEmail: source.contactEmail ?? '',
    contactPhone: source.contactPhone ?? '',
  };
}

function copyStop(
  source: IDecaDocumentStop,
  empty: ReturnType<typeof getEmptyIDecaDocumentStop>,
  keepDate = false,
): IDecaDocumentStop {
  return {
    ...empty,
    companyName: source.companyName ?? '',
    street: source.street ?? '',
    city: source.city ?? '',
    postalCode: source.postalCode ?? '',
    stateCode: source.stateCode ?? '',
    countryCode: source.countryCode ?? '',
    ...(keepDate ? { date: source.date } : {}),
  };
}
